import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { UncontrolledTooltip } from "reactstrap";

import Breadcrumb from "../../../../components/common/breadcrumb";
import { showToast } from "../../../../components/ToastifyNotification";

import { DeleteDeathCertificate, GetDeathCertificates } from "../../../../api/ipd/deathCertificateAPI";
import { DeleteIpdConsultantChange, GetIpdConsultantChanges } from "../../../../api/ipd/ipdConsultantChangeAPI";

const ListIpdConsultantChange = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ================= FETCH DATA =================
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetIpdConsultantChanges();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          ipdNo: item?.ipdEntryId?.ipdNo || "",
          patientName: item?.ipdEntryId?.patientId?.patientName || "",
          uhidNo: item?.ipdEntryId?.patientId?.uhidNo || "",
          admitDate: item?.ipdEntryId?.createdAt.split("T")[0] || "",

          oldConsultant: item.oldConsultantId?.name || "",
          newConsultant: item.newConsultantId?.name || "",
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= SEARCH =================
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  // ================= ACTIONS =================
  const handleEditClick = id => {
    navigate(`/ipd/transaction/ipd-consultant-change/edit/${id}`);
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This delivery entry will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteIpdConsultantChange(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    { name: "S.No", selector: row => row.index, width: "6%" },

    { name: "IPD No", selector: row => row.ipdNo, width: "15%" },
    { name: "Patient Name", selector: row => row.patientName, width: "15%" },
    { name: "UHID No", selector: row => row.uhidNo, width: "15%" },
    { name: "Admit Date", selector: row => row.admitDate, width: "15%" },
    { name: "Old Consultant", selector: row => row.oldConsultant, width: "15%" },
    { name: "New Consultant", selector: row => row.newConsultant, width: "15%" },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "20%"
    }
  ];

  // ================= CSV EXPORT =================
  const headers = [
    { label: "S.No", key: "index" },

    { label: "IPD No", key: "ipdNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "UHID No", key: "uhidNo" },
    { label: "Admit Date", key: "admitDate" },

    { label: "Old Consultant", key: "oldConsultant" },
    { label: "New Consultant", key: "newConsultant" },
  ];

  const ExportCSV = ({ data }) =>
      <CSVLink data={data} headers={headers} filename="ipd-consultant-change-entries.csv">
        <button className="btn btn-primary mt-1" id="export">
          Export
        </button>
        <UncontrolledTooltip placement="top" target="export">
          Export to CSV
        </UncontrolledTooltip>
      </CSVLink>;

  // ================= RENDER =================
  return (
    <Fragment>
      <Breadcrumb parent="Ipd Consultant Change" title="Ipd Consultant Change" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Ipd Consultant Change</h5>

            <Link to={`/ipd/transaction/ipd-consultant-change/add`}>
              <button className="btn btn-primary" id="create">
                Create Ipd Consultant Change
              </button>
              <UncontrolledTooltip placement="top" target="create">
                Create New Ipd Consultant Change
              </UncontrolledTooltip>
            </Link>
          </div>

          <div className="card-body">
            <div className="row mb-2">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="col-md-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListIpdConsultantChange;
